import React, { useEffect, useRef, useState } from "react";
import IconLoader from "@/features/Common/components/IconLoader";
import { Box, Button } from "@mui/material";
import WellnessCategoryCard from "../WellnessCategoryCard";
import { useTranslation } from "react-i18next";
import { metrics } from "./data";
import HealthScoreIndicator from "../Common/HealthScoreIndicator";
import CollapsableMetricsList from "../Common/CollapsableMetricsList";
import {
  WellnessCategoryCardProps,
  WellnessScoreStatus,
} from "../Common/types";
import { useNavigate } from "react-router-dom";
import { trackEvent } from "@/services/analytics-adapter";
import { useMachine } from "@xstate/react";
import {
  vitalsCategoryMachine,
  vitalsCategoryEventNames,
  VitalsCategoryState,
} from "./machine";
import MyBloodPressureScreen from "./BloodPressure";
import type { BloodPressureDataPoint } from "./BloodPressure/BloodPressureChart";
import MyHeartRateScreen from "./HeartRate";
import type { HeartRateDataPoint } from "./HeartRate/HeartRateChart";
import MyBreathingRateScreen from "./BreathingRate";
import type { BreathingRateDataPoint } from "./BreathingRate/BreathingRateChart";
import { MetricItem } from "../Common/types";
import BloodPressureManualEntry from "@/features/HealthScore/pages/BloodPressureManualEntry";
import HeartRateManualEntry from "@/features/HealthScore/pages/HeartRateManualEntry";
import BreathingRateManualEntry from "@/features/HealthScore/pages/BreathingRateManualEntry";

const fullScreenOverlaySx = {
  position: "fixed" as const,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1300,
  bgcolor: "background.paper",
  overflow: "auto",
};

const sampleBloodPressureChartData: BloodPressureDataPoint[] = [
  { date: "20/01", systolic: 132, diastolic: 80, fullDate: "20.01.2025" },
  {
    date: "02/04",
    systolic: 127,
    diastolic: 76,
    fullDate: "02.04.2025",
    isSelected: true,
  },
];

const sampleHeartRateChartData: HeartRateDataPoint[] = [
  { date: "20/01", heartRate: 85, fullDate: "20.01.2025" },
  {
    date: "02/04",
    heartRate: 68,
    fullDate: "02.04.2025",
    isSelected: true,
  },
];

const sampleBreathingRateChartData: BreathingRateDataPoint[] = [
  { date: "20/01", breathingRate: 15, fullDate: "20.01.2025" },
  {
    date: "02/04",
    breathingRate: 14,
    fullDate: "02.04.2025",
    isSelected: true,
  },
];

function handleMetricClick(
  metric: MetricItem,
  send: (event: { type: string }) => void
) {
  if (metric.id === "bp")
    send({ type: vitalsCategoryEventNames.OPEN_BLOOD_PRESSURE });
  else if (metric.id === "hr")
    send({ type: vitalsCategoryEventNames.OPEN_HEART_RATE });
  else if (metric.id === "breathing")
    send({ type: vitalsCategoryEventNames.OPEN_BREATHING_RATE });
}

export default function VitalsCategory() {
  const { t } = useTranslation();
  const [score, setScore] = useState<number | null>(null);
  const navigate = useNavigate();
  const [state, send] = useMachine(vitalsCategoryMachine);
  const vitalsSectionRef = useRef<HTMLDivElement>(null);
  const prevStateRef = useRef(state.value);

  useEffect(() => {
    setScore(58);
  }, []);

  const [listExpanded, setListExpanded] = useState(false);

  useEffect(() => {
    const wasDetailOrFormScreen =
      prevStateRef.current === VitalsCategoryState.BloodPressure ||
      prevStateRef.current === VitalsCategoryState.HeartRate ||
      prevStateRef.current === VitalsCategoryState.BreathingRate ||
      prevStateRef.current === VitalsCategoryState.BloodPressureForm ||
      prevStateRef.current === VitalsCategoryState.HeartRateForm ||
      prevStateRef.current === VitalsCategoryState.BreathingRateForm;
    if (wasDetailOrFormScreen && state.matches(VitalsCategoryState.Dashboard)) {
      setListExpanded(true);
      requestAnimationFrame(() => {
        vitalsSectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    }
    prevStateRef.current = state.value;
  }, [state.value]);

  function startFaceScan() {
    trackEvent({
      event: "action.healthCheckManuallyInitiated",
      source: "Health Score - vitals",
    });
    navigate("/binah-scan?skipScanPrompt=true");
  }

  function handleBack() {
    send({ type: vitalsCategoryEventNames.BACK });
  }

  function handleBackToDetail() {
    send({ type: vitalsCategoryEventNames.BACK_TO_DETAIL });
  }

  const vitalProps: WellnessCategoryCardProps = {
    icon: (
      <IconLoader icon="VitalsIcon" color="primary" sx={{ fontSize: 40 }} />
    ),
    title: "Vitals",
    status: WellnessScoreStatus.NeedsAttention,
    description: score
      ? t("Vitals.description.update")
      : t("Vitals.description"),
    lastUpdated: "2026-01-15T11:25:27.3778572+00:00",
  };

  if (state.matches(VitalsCategoryState.BloodPressureForm)) {
    return (
      <Box sx={fullScreenOverlaySx}>
        <BloodPressureManualEntry
          onBack={handleBackToDetail}
          onCancel={handleBackToDetail}
          onSave={handleBackToDetail}
        />
      </Box>
    );
  }

  if (state.matches(VitalsCategoryState.HeartRateForm)) {
    return (
      <Box sx={fullScreenOverlaySx}>
        <HeartRateManualEntry
          onBack={handleBackToDetail}
          onCancel={handleBackToDetail}
          onSave={handleBackToDetail}
        />
      </Box>
    );
  }

  if (state.matches(VitalsCategoryState.BreathingRateForm)) {
    return (
      <Box sx={fullScreenOverlaySx}>
        <BreathingRateManualEntry
          onBack={handleBackToDetail}
          onCancel={handleBackToDetail}
          onSave={handleBackToDetail}
        />
      </Box>
    );
  }

  if (state.matches(VitalsCategoryState.BloodPressure)) {
    return (
      <Box sx={fullScreenOverlaySx}>
        <MyBloodPressureScreen
          chartData={sampleBloodPressureChartData}
          status={WellnessScoreStatus.Healthy}
          lastUpdated="2026-01-15T11:25:27.3778572+00:00"
          onBack={handleBack}
          onOpenManualEntry={() =>
            send({ type: vitalsCategoryEventNames.OPEN_BP_FORM })
          }
        />
      </Box>
    );
  }

  if (state.matches(VitalsCategoryState.HeartRate)) {
    return (
      <Box sx={fullScreenOverlaySx}>
        <MyHeartRateScreen
          chartData={sampleHeartRateChartData}
          status={WellnessScoreStatus.Healthy}
          lastUpdated="2026-01-15T11:25:27.3778572+00:00"
          onBack={handleBack}
          onOpenManualEntry={() =>
            send({ type: vitalsCategoryEventNames.OPEN_HR_FORM })
          }
        />
      </Box>
    );
  }

  if (state.matches(VitalsCategoryState.BreathingRate)) {
    return (
      <Box sx={fullScreenOverlaySx}>
        <MyBreathingRateScreen
          metricValue="14"
          status={WellnessScoreStatus.Healthy}
          lastUpdated="2026-01-15T11:25:27.3778572+00:00"
          chartData={sampleBreathingRateChartData}
          onBack={handleBack}
          onOpenManualEntry={() =>
            send({ type: vitalsCategoryEventNames.OPEN_BREATHING_FORM })
          }
        />
      </Box>
    );
  }

  return (
    <Box ref={vitalsSectionRef}>
      <WellnessCategoryCard {...vitalProps}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            mt: 3,
          }}
        >
          {score && <HealthScoreIndicator healthScore={score} />}

          {score && (
            <CollapsableMetricsList
              metrics={metrics}
              onMetricClick={(metric) => handleMetricClick(metric, send)}
              expanded={listExpanded}
              onExpandedChange={setListExpanded}
            />
          )}
        </Box>

        <Button
          variant="contained"
          color="primary"
          onClick={startFaceScan}
          fullWidth
          startIcon={
            score ? null : <IconLoader icon="SmilingFaceInRoundedSquareIcon" />
          }
        >
          {score
            ? t("Vitals.buttonLabel.primary.update")
            : t("Vitals.buttonLabel.primary")}
        </Button>
      </WellnessCategoryCard>
    </Box>
  );
}
